import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import EmailIcon from '@/assets/svg/envelope.svg?react';
import Google from '@/assets/svg/google.svg?react';
import LockIcon from '@/assets/svg/lock.svg?react';
import Microsoft from '@/assets/svg/microsoft.svg?react';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import type AuthError from '@/error/AuthError';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import {
  getPreferredSignInMethod,
  setPendingSignInMethod,
  type SignInMethod,
} from '@/helper/user-preferences';
import useLocationState from '@/hooks/useLocationState';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import AuthMethodButtons, { type AuthMethodConfig } from './AuthMethodButtons';
import DiscoveryAuthenticate from './DiscoveryAuthenticate';
import SignInPanel from './SignInPanel';
import TermsOfUseLink from './TermsOfUseLink';
import { type OAuthProviderType } from './type';

const SignIn = () => {
  const { error } = useLocationState<{ error?: AuthError }>();
  const { pushNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stytch = useStytchB2BClient();

  const preferredMethod = useMemo(() => getPreferredSignInMethod(), []);
  const redirectUrl = `${window.location.origin}${ROUTES.signIn.path}`;

  const handleOAuthProviderClick = async (provider: OAuthProviderType) => {
    setPendingSignInMethod(provider);

    try {
      await stytch.oauth[provider].discovery.start({
        discovery_redirect_url: redirectUrl,
      });
    } catch (e) {
      pushNotification({
        type: 'error',
        message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
      });
    }
  };

  const handleSsoClick = async () => {
    const connectionId =
      searchParams.get('sso_connection_id') ?? searchParams.get('connection_id') ?? '';

    if (!connectionId) {
      navigate(
        `${ROUTES.signInSso.path}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`,
      );
      return;
    }

    setPendingSignInMethod('sso');

    try {
      await stytch.sso.start({
        connection_id: connectionId,
        login_redirect_url: redirectUrl,
      });
    } catch (e) {
      pushNotification({
        type: 'error',
        message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
      });
    }
  };

  const signInEmailPath = `${ROUTES.signInEmail.path}${
    searchParams.size > 0 ? `?${searchParams.toString()}` : ''
  }`;
  const signUpPath = `${ROUTES.signUp.path}${
    searchParams.size > 0 ? `?${searchParams.toString()}` : ''
  }`;

  const methodLabelById: Record<SignInMethod, string> = {
    google: t`Google`,
    microsoft: t`Microsoft`,
    sso: t`SSO`,
    email: t`email`,
  };

  const handleEmailClick = () => {
    navigate(signInEmailPath);
  };

  const signInMethods: AuthMethodConfig[] = useMemo(
    () => [
      {
        id: 'google',
        label: t`Continue with Google`,
        icon: <Google />,
        onClick: () => handleOAuthProviderClick('google'),
        rateLimit: true,
      },
      {
        id: 'microsoft',
        label: t`Continue with Microsoft`,
        icon: <Microsoft />,
        onClick: () => handleOAuthProviderClick('microsoft'),
        rateLimit: true,
      },
      {
        id: 'sso',
        label: t`Continue with SSO`,
        icon: <LockIcon />,
        onClick: handleSsoClick,
        rateLimit: true,
      },
      {
        id: 'email',
        label: t`Continue with email`,
        icon: <EmailIcon />,
        onClick: handleEmailClick,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signInEmailPath],
  );

  useEffect(() => {
    if (error) {
      pushNotification({ type: 'error', message: error.errorMessage });
    }
  }, [error, pushNotification]);

  return (
    <FullPage title={t`Sign In`}>
      <DiscoveryAuthenticate>
        <SignInPanel
          title={t`Sign In to Channel99`}
          footer={
            <>
              <Text variant="caption1" color="grey">
                <Trans>
                  Don't have an account?{' '}
                  <Link variant="caption1" color="aqua" to={signUpPath}>
                    Create an account
                  </Link>
                </Trans>
              </Text>
              <Text variant="caption1" color="grey">
                <Trans>
                  By continuing to sign in I agree to the <TermsOfUseLink />
                </Trans>
              </Text>
            </>
          }
          showGap
        >
          <AuthMethodButtons
            methods={signInMethods}
            preferredMethodId={preferredMethod}
            methodLabels={methodLabelById}
          />
        </SignInPanel>
      </DiscoveryAuthenticate>
    </FullPage>
  );
};

export default SignIn;
